import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { GridFilterItem, GridLinkOperator } from '@mui/x-data-grid/models/gridFilterItem';
import { GridEnrichedColDef } from '@mui/x-data-grid';
import {
  GenericModelList,
  GenericRelatedModelList,
  Item,
  SumRowsType,
  useAPIWrapper,
} from 'drf-react-by-schema';
import { Link, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { ENTREPOSTO_VIEWS, GENERAL_ADMINS, Permission } from 'src/constants/Permission';
import { Layout } from 'src/styles';
import { FlocosProductionStatus } from 'src/constants';
import { useAppSelector } from 'src/app/hooks';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

import FlocosProductionCard from 'src/components/FlocosProductionCard';
import FlocosNoResultsCard from 'src/components/FlocosNoResultsCard';
import StatusChip from 'src/components/StatusChip';
import { ProductionKey } from 'src/constants/ProductionKey';
import { ProductionZone } from 'src/constants/ProductionZone';
import { ListHolder } from 'src/styles/styledComponents';
import { appDispatch } from 'src/utils';
import { setPage } from 'src/features/pageSlice';

const ORDERED_PRODUCTION_FILTERS = [
  FlocosProductionStatus.READY_TO_REVIEW,
  FlocosProductionStatus.IN_PRODUCTION,
  FlocosProductionStatus.REVIEWED,
  FlocosProductionStatus.COMPLETE,
];

export default function FlocosProductionList() {
  const { perfil, isMobile } = useAppSelector((state) => state.page);
  const { getGenericModel } = useAPIWrapper();
  const [isAdmin, isGroupAdmin, isEntrepostoView] = useHasPermissionsMulti([
    GENERAL_ADMINS,
    [Permission.COORDENADORA_GRUPO],
    ENTREPOSTO_VIEWS,
  ]);
  const [filters, setFilters] = useState<GridFilterModel>();
  const [checkedStatus, setCheckedStatus] = useState<FlocosProductionStatus>();

  const [URLSearchParams] = useSearchParams();
  const statusFilterFromURL = URLSearchParams.get('status') as FlocosProductionStatus | null;
  const zoneFilterFromURL =
    (URLSearchParams.get('zoneFilter') as ProductionZone | null) || ProductionZone.SELF;
  const perfilIdFromURL = URLSearchParams.get('perfil');

  const model = 'flocos_production';
  const label = 'Produção de flocos';

  const indexField = 'lote';

  const checkedZone = useMemo(() => {
    return (
      (filters?.items.find(({ columnField }) =>
        [ProductionZone.SELF, ProductionZone.GROUP, ProductionZone.COMMUNITY].includes(
          columnField as ProductionZone
        )
      )?.columnField as ProductionZone) || zoneFilterFromURL
    );
  }, [filters?.items, zoneFilterFromURL]);

  const hiddenFields = undefined;

  const columnFields = useMemo(() => {
    const newColumnFields: string[] = ['lote', 'data_registro'];
    if (isAdmin || isEntrepostoView) {
      newColumnFields.push(...['perfil', 'grupo', 'comunidade']);
    } else if (isGroupAdmin && checkedZone !== ProductionZone.SELF) {
      newColumnFields.push(...['perfil']);
    }

    newColumnFields.push(
      ...[
        'status',
        'peso_flocos_seco',
        'data_flocos_seco',
        'peso_flocos_umido',
        'data_flocos_umido',
        'peso_coleta',
        'data_coleta',
        'ajudantes',
      ]
    );

    return newColumnFields;
  }, [isAdmin, isGroupAdmin, isEntrepostoView, checkedZone]);

  const minWidthFields = useMemo(() => {
    const newMinwidthFields = {
      data_registro: 150,
      ...(isAdmin && {
        grupo: 100,
        comunidadeProduction: 150,
      }),
      status: 120,
      data_flocos_seco: 100,
      data_flocos_umido: 100,
      data_coleta: 100,
      ajudantes: 150,
    };
    return newMinwidthFields;
  }, [isAdmin]);

  const customColumnOperations: (column: GridEnrichedColDef) => GridEnrichedColDef = useCallback(
    (column) => {
      if (column.field === 'status') {
        column.renderCell = (params: Item) => {
          return (
            <StatusChip
              type={ProductionKey.FLOCOS}
              status={params.row.status.value as FlocosProductionStatus}
            />
          );
        };
      }
      return column;
    },
    []
  );

  const sumRows: SumRowsType | undefined = useMemo(() => {
    let prefix: string = '';
    switch (checkedStatus) {
      case FlocosProductionStatus.READY_TO_REVIEW:
        prefix = 'Peso de flocos a revisar: ';
        break;
      case FlocosProductionStatus.REVIEWED:
        prefix = 'Peso de produções já revisadas e ainda não beneficiadas: ';
        break;
      case FlocosProductionStatus.COMPLETE:
        prefix = 'Total de flocos produzidos: ';
        break;
    }
    return !checkedStatus || checkedStatus !== FlocosProductionStatus.IN_PRODUCTION
      ? {
          rows: [
            {
              field: 'peso_flocos_seco',
              prefix,
              suffix: ' Kg',
            },
            // {},
          ],
          severity: 'info',
        }
      : undefined;
  }, [checkedStatus]);

  const defaultFilter = useMemo(() => {
    if (!perfil) {
      return;
    }

    const emptyItems: GridFilterItem[] = checkedStatus
      ? [
          {
            columnField: 'status',
            value: checkedStatus,
            operatorValue: 'equals',
          },
        ]
      : [];

    if (isGroupAdmin) {
      switch (checkedZone) {
        case ProductionZone.SELF:
          emptyItems.push({
            columnField: ProductionZone.SELF,
          });
          break;
        case ProductionZone.GROUP:
          if (perfil.grupo) {
            emptyItems.push({
              columnField: ProductionZone.GROUP,
              value: perfil.grupo.id,
              operatorValue: 'equals',
            });
          }
          break;
        case ProductionZone.COMMUNITY:
          if (perfil.comunidade) {
            emptyItems.push({
              columnField: ProductionZone.COMMUNITY,
              value: perfil.comunidade.id,
              operatorValue: 'equals',
            });
          }
          break;
      }
    }

    const newDefaultFilter = {
      linkOperator: GridLinkOperator.And,
      items: [...emptyItems],
    };

    return newDefaultFilter;
  }, [isGroupAdmin, perfil, checkedZone, checkedStatus]);

  const handleClickFilter = useCallback(
    ({
      statusClicked,
      zoneClicked,
    }: {
      statusClicked?: FlocosProductionStatus;
      zoneClicked?: ProductionZone;
    }) => {
      if (!perfil || !defaultFilter || (!statusClicked && !zoneClicked)) {
        return;
      }

      if (statusClicked) {
        setCheckedStatus(statusClicked === checkedStatus ? undefined : statusClicked);
        return;
      }

      if (zoneClicked) {
        const newItems = defaultFilter.items.filter(({ columnField }) => columnField === 'status');

        switch (zoneClicked) {
          case ProductionZone.SELF:
            newItems.push({
              columnField: ProductionZone.SELF,
            });
            break;
          case ProductionZone.GROUP:
            if (perfil.grupo) {
              newItems.push({
                columnField: ProductionZone.GROUP,
                value: perfil.grupo.id,
                operatorValue: 'equals',
              });
            }
            break;
          case ProductionZone.COMMUNITY:
            if (perfil.comunidade) {
              newItems.push({
                columnField: ProductionZone.COMMUNITY,
                value: perfil.comunidade.id,
                operatorValue: 'equals',
              });
            }
            break;
        }

        setFilters({
          ...defaultFilter,
          items: newItems,
        });
        return;
      }
    },
    [perfil, defaultFilter, checkedStatus]
  );

  const handleMine = () => {
    handleClickFilter({ zoneClicked: ProductionZone.SELF });
  };
  const handleGrupo = () => {
    handleClickFilter({ zoneClicked: ProductionZone.GROUP });
  };
  const handleComunidade = () => {
    handleClickFilter({ zoneClicked: ProductionZone.COMMUNITY });
  };

  useEffect(() => {
    setFilters(defaultFilter);
  }, [defaultFilter]);

  useEffect(() => {
    if (statusFilterFromURL) {
      setCheckedStatus(statusFilterFromURL);
    }
  }, [statusFilterFromURL]);

  useEffect(() => {
    if (!perfilIdFromURL || !isAdmin) {
      appDispatch(
        setPage({
          subTitle: null,
        })
      );
      return;
    }

    const fetchPerfil = async () => {
      const newPerfilFromURL = await getGenericModel({
        model: 'perfil',
        id: perfilIdFromURL,
      });

      if (newPerfilFromURL !== false) {
        appDispatch(
          setPage({
            subTitle: newPerfilFromURL.data.nome,
          })
        );
      }
    };

    fetchPerfil();
  }, [perfilIdFromURL, isAdmin]);

  if (!perfil || !filters) {
    return null;
  }

  return (
    <ListHolder isMobile={isMobile}>
      {isGroupAdmin && (perfil.grupo || perfil.comunidade) && (
        <Box sx={{ mb: 2 }}>
          <Button
            onClick={checkedZone !== ProductionZone.SELF ? handleMine : undefined}
            size="small"
            variant={checkedZone === ProductionZone.SELF ? 'contained' : 'outlined'}>
            Minha produção
          </Button>
          {perfil.grupo && (
            <Button
              onClick={checkedZone !== ProductionZone.GROUP ? handleGrupo : undefined}
              size="small"
              variant={checkedZone === ProductionZone.GROUP ? 'contained' : 'outlined'}
              sx={{ ml: 2 }}>
              Grupo
            </Button>
          )}
          {perfil.comunidade && (
            <Button
              onClick={checkedZone !== ProductionZone.COMMUNITY ? handleComunidade : undefined}
              size="small"
              variant={checkedZone === ProductionZone.COMMUNITY ? 'contained' : 'outlined'}
              sx={{ ml: 2 }}>
              Comunidade
            </Button>
          )}
        </Box>
      )}
      <Box sx={Layout.topBarOverListWithScrollX}>
        {ORDERED_PRODUCTION_FILTERS.map((status) => (
          <StatusChip
            key={`filter_${status}`}
            type={ProductionKey.FLOCOS}
            variant={checkedStatus === status ? 'filled' : 'outlined'}
            onClick={() => {
              handleClickFilter({ statusClicked: status });
            }}
            status={status}
            size="medium"
          />
        ))}
      </Box>
      {(isAdmin && !perfilIdFromURL) || isEntrepostoView || checkedZone !== ProductionZone.SELF ? (
        <GenericModelList
          model={model}
          hasHeader={false}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${model}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={FlocosProductionCard}
          MobileRenderNoResults={FlocosNoResultsCard}
          tableAutoHeight={false}
          defaultFilter={filters}
          hideQuickFilterBar={!isAdmin && !isEntrepostoView}
        />
      ) : (
        <GenericRelatedModelList
          model="perfil"
          id={isAdmin && perfilIdFromURL ? perfilIdFromURL : perfil.id}
          relatedModel={model}
          label={label}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${model}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={FlocosProductionCard}
          MobileRenderNoResults={FlocosNoResultsCard}
          tableAutoHeight={true}
          noCardWrapper={true}
          defaultFilter={filters}
          hideQuickFilterBar={!isAdmin && !isEntrepostoView}
        />
      )}
    </ListHolder>
  );
}
